export interface RoomCodeMatchTarget {
  roomId: string;
  roomName: string;
}

export function extractFirstUppercaseLetter(text: string) {
  const match = text.match(/[A-Z]/);
  return match ? match[0] : null;
}

export function extractSeriesPrefix(text: string) {
  const matches = [...text.matchAll(/[A-Z]+/g)];
  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    if (!match || match.index === undefined) continue;
    const start = match.index + match[0].length;
    const end = matches[index + 1]?.index ?? text.length;
    if (/\d+/.test(text.slice(start, end))) {
      return match[0];
    }
  }
  return matches[0]?.[0] ?? null;
}

const MAX_RANGE_SIZE = 500;

export function parseRoomCodePatterns(input: string) {
  const tokens = new Set<string>();
  const parts = input
    .split(/[,，\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    throw new Error("请输入群编号匹配规则");
  }

  for (const part of parts) {
    const rangeMatch = part.match(/^([A-Za-z_-]*?)(\d+)\s*-\s*([A-Za-z_-]*?)(\d+)$/);
    if (rangeMatch) {
      const prefix = rangeMatch[1] || "";
      const endPrefix = rangeMatch[3] || "";
      if (prefix !== endPrefix) {
        throw new Error(`范围前缀不一致：${part}`);
      }

      const startText = rangeMatch[2] || "";
      const endText = rangeMatch[4] || "";
      const width = Math.max(startText.length, endText.length);
      const start = Number(startText);
      const end = Number(endText);

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        throw new Error(`范围数字无效：${part}`);
      }
      if (end < start) {
        throw new Error(`范围起止顺序错误：${part}`);
      }
      if (end - start + 1 > MAX_RANGE_SIZE) {
        throw new Error(`单个范围最多支持 ${MAX_RANGE_SIZE} 个编号`);
      }

      for (let current = start; current <= end; current += 1) {
        tokens.add(`${prefix}${String(current).padStart(width, "0")}`);
      }
      continue;
    }

    tokens.add(part);
  }

  return Array.from(tokens);
}

export function roomNameMatchesCode(roomName: string, code: string) {
  if (!roomName || !code) return false;

  let fromIndex = 0;
  while (fromIndex < roomName.length) {
    const index = roomName.indexOf(code, fromIndex);
    if (index === -1) break;

    const before = index > 0 ? roomName[index - 1] : "";
    const after = roomName[index + code.length] || "";
    const codeEndsWithDigit = /\d/.test(code[code.length - 1] || "");

    const validBefore = !before || !/[A-Za-z0-9]/.test(before);
    const validAfter = !codeEndsWithDigit || !after || !/\d/.test(after);

    if (validBefore && validAfter) {
      return true;
    }

    fromIndex = index + 1;
  }

  const parsedCode = parseRoomCode(code);
  if (!parsedCode?.prefix) return false;

  return extractSeriesCode(roomName, parsedCode.prefix) === code;
}

export function filterRoomsByNameKeyword<T extends RoomCodeMatchTarget>(
  rooms: T[],
  keyword?: string
) {
  const trimmed = keyword?.trim();
  if (!trimmed) return rooms;
  const lower = trimmed.toLowerCase();
  return rooms.filter((room) => (room.roomName || room.roomId).toLowerCase().includes(lower));
}

export function matchRoomsByCodes<T extends RoomCodeMatchTarget>(rooms: T[], codes: string[]) {
  const matched: T[] = [];
  const seenRoomIds = new Set<string>();

  for (const room of rooms) {
    const roomName = room.roomName || room.roomId;
    if (codes.some((code) => roomNameMatchesCode(roomName, code)) && !seenRoomIds.has(room.roomId)) {
      seenRoomIds.add(room.roomId);
      matched.push(room);
    }
  }

  return matched;
}

function parseRoomCode(code: string) {
  const match = code.match(/^([A-Za-z_-]*?)(\d+)$/);
  if (!match) return null;

  const prefix = match[1] || "";
  const numberText = match[2] || "";
  const number = Number(numberText);
  if (!Number.isFinite(number)) return null;

  return { prefix, number, width: numberText.length };
}

export function formatRoomCodeRanges(codes: string[]) {
  if (codes.length === 0) return [];

  const parseable: Array<{ code: string; prefix: string; number: number; width: number }> = [];
  const unparseable: string[] = [];

  for (const code of codes) {
    const parsed = parseRoomCode(code);
    if (!parsed) {
      unparseable.push(code);
      continue;
    }
    parseable.push({ code, ...parsed });
  }

  parseable.sort((a, b) => {
    const prefixCmp = a.prefix.localeCompare(b.prefix);
    if (prefixCmp !== 0) return prefixCmp;
    if (a.width !== b.width) return a.width - b.width;
    return a.number - b.number;
  });

  const ranges: string[] = [];

  for (let index = 0; index < parseable.length; ) {
    const start = parseable[index]!;
    let end = start;
    let nextIndex = index + 1;

    while (nextIndex < parseable.length) {
      const next = parseable[nextIndex]!;
      if (next.prefix === end.prefix && next.width === end.width && next.number === end.number + 1) {
        end = next;
        nextIndex += 1;
        continue;
      }
      break;
    }

    if (start.number === end.number) {
      ranges.push(start.code);
    } else {
      const startText = `${start.prefix}${String(start.number).padStart(start.width, "0")}`;
      const endText = `${end.prefix}${String(end.number).padStart(end.width, "0")}`;
      ranges.push(`${startText}-${endText}`);
    }

    index = nextIndex;
  }

  return [...ranges, ...unparseable];
}

export function extractSeriesCode(roomName: string, seriesLetter: string) {
  if (!roomName || !seriesLetter) return null;

  const escapedPrefix = seriesLetter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(escapedPrefix, "g");
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(roomName)) !== null) {
    const index = match.index;
    const before = index > 0 ? roomName[index - 1] : "";
    const after = roomName[index + match[0].length] || "";
    const validBefore = !before || !/[A-Za-z0-9]/.test(before);
    const validAfter = !after || !/[A-Z]/.test(after);
    if (!validBefore || !validAfter) continue;

    const remainder = roomName.slice(index + match[0].length);
    const nextPrefixIndex = remainder.search(/[A-Z]+/);
    const candidateRegion =
      nextPrefixIndex >= 0 ? remainder.slice(0, nextPrefixIndex) : remainder;
    const numberMatch = candidateRegion.match(/\d+/);
    if (numberMatch) return `${seriesLetter}${numberMatch[0]}`;
  }

  return null;
}

export interface SeriesGapReport {
  seriesLetter: string;
  totalRooms: number;
  parsedCount: number;
  unparseableRooms: RoomCodeMatchTarget[];
  prefix: string;
  width: number;
  expectedMin: number;
  expectedMax: number;
  missingCodes: string[];
  missingRanges: string[];
  isComplete: boolean;
}

export function detectSeriesSequenceGaps<T extends RoomCodeMatchTarget>(
  rooms: T[],
  seriesLetter: string
): SeriesGapReport | null {
  const seriesRooms = rooms.filter((room) => {
    const roomName = room.roomName || room.roomId;
    return extractSeriesPrefix(roomName) === seriesLetter;
  });

  if (seriesRooms.length === 0) {
    return null;
  }

  const parsedByRoom = new Map<T, { code: string; prefix: string; number: number; width: number }>();
  const unparseableRooms: T[] = [];

  for (const room of seriesRooms) {
    const roomName = room.roomName || room.roomId;
    const code = extractSeriesCode(roomName, seriesLetter);
    if (!code) {
      unparseableRooms.push(room);
      continue;
    }

    const parsed = parseRoomCode(code);
    if (!parsed) {
      unparseableRooms.push(room);
      continue;
    }

    parsedByRoom.set(room, { code, ...parsed });
  }

  const parsedEntries = Array.from(parsedByRoom.values());
  if (parsedEntries.length === 0) {
    return {
      seriesLetter,
      totalRooms: seriesRooms.length,
      parsedCount: 0,
      unparseableRooms,
      prefix: seriesLetter,
      width: 0,
      expectedMin: 0,
      expectedMax: 0,
      missingCodes: [],
      missingRanges: [],
      isComplete: unparseableRooms.length === 0,
    };
  }

  const shapeCounts = new Map<string, { prefix: string; width: number; count: number }>();
  for (const entry of parsedEntries) {
    const key = `${entry.prefix}\0${entry.width}`;
    const current = shapeCounts.get(key);
    if (current) {
      current.count += 1;
    } else {
      shapeCounts.set(key, { prefix: entry.prefix, width: entry.width, count: 1 });
    }
  }

  const dominantShape = Array.from(shapeCounts.values()).sort((a, b) => b.count - a.count)[0]!;
  const { prefix, width } = dominantShape;

  const existingNumbers = new Set<number>();
  for (const entry of parsedEntries) {
    if (entry.prefix === prefix && entry.width === width) {
      existingNumbers.add(entry.number);
    }
  }

  if (existingNumbers.size === 0) {
    return {
      seriesLetter,
      totalRooms: seriesRooms.length,
      parsedCount: 0,
      unparseableRooms,
      prefix,
      width,
      expectedMin: 0,
      expectedMax: 0,
      missingCodes: [],
      missingRanges: [],
      isComplete: false,
    };
  }

  const expectedMin = Math.min(...existingNumbers);
  const expectedMax = Math.max(...existingNumbers);

  const missingCodes: string[] = [];
  for (let current = expectedMin; current <= expectedMax; current += 1) {
    if (!existingNumbers.has(current)) {
      missingCodes.push(`${prefix}${String(current).padStart(width, "0")}`);
    }
  }

  return {
    seriesLetter,
    totalRooms: seriesRooms.length,
    parsedCount: existingNumbers.size,
    unparseableRooms,
    prefix,
    width,
    expectedMin,
    expectedMax,
    missingCodes,
    missingRanges: formatRoomCodeRanges(missingCodes),
    isComplete: missingCodes.length === 0 && unparseableRooms.length === 0,
  };
}

export function summarizeRoomCodeMatches<T extends RoomCodeMatchTarget>(
  rooms: T[],
  pattern: string,
  nicknameFilter?: string
) {
  const codes = parseRoomCodePatterns(pattern);
  const matched = filterRoomsByNameKeyword(matchRoomsByCodes(rooms, codes), nicknameFilter);
  const matchedCodes = codes.filter((code) =>
    matched.some((room) => roomNameMatchesCode(room.roomName || room.roomId, code))
  );
  const unmatchedCodes = codes.filter((code) => !matchedCodes.includes(code));

  return {
    codes,
    matched,
    matchedCodes,
    unmatchedCodes,
  };
}
